import { useEffect, useRef } from 'react';
import * as THREE from 'three';

/**
 * Controlled equirectangular 360 viewer.
 *
 * Renders the panorama on the inside of a sphere (``BackSide``) with a
 * perspective camera sitting at the sphere's centre, so the image wraps
 * around the viewer like a real 360 photo. The component is fully
 * controlled: ``yaw``/``pitch``/``fov`` come in as props and pointer
 * drags are reported back through ``onChange`` rather than held in local
 * state, which lets a parent synchronise two viewers off one source of
 * truth.
 */
export interface Panorama360Props {
  url: string;
  /** Horizontal look angle in radians. */
  yaw: number;
  /** Vertical look angle in radians (clamped to ~±85°). */
  pitch: number;
  /** Vertical field of view in degrees (smaller = zoomed in). */
  fov: number;
  onChange: (next: { yaw: number; pitch: number }) => void;
  /** Reports a new field of view on wheel zoom. */
  onFovChange?: (fov: number) => void;
  className?: string;
}

// Keep the camera from flipping over the poles.
const PITCH_LIMIT = (85 * Math.PI) / 180;
// Drag sensitivity: screen pixels → radians.
const DRAG_SPEED = 0.005;
// Zoom (field-of-view) bounds in degrees and wheel sensitivity.
const MIN_FOV = 20;
const MAX_FOV = 100;
const FOV_SPEED = 0.05;

export function Panorama360({
  url,
  yaw,
  pitch,
  fov,
  onChange,
  onFovChange,
  className,
}: Panorama360Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Latest controlled angles, mirrored into a ref so the rAF loop and
  // pointer handlers read fresh values without re-subscribing.
  const anglesRef = useRef({ yaw, pitch });
  anglesRef.current = { yaw, pitch };

  // Stable handle so pointer math reports deltas through the freshest
  // callback without re-running the heavy three.js setup effect.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const fovRef = useRef(fov);
  fovRef.current = fov;

  const onFovChangeRef = useRef(onFovChange);
  onFovChangeRef.current = onFovChange;

  // three.js setup: one renderer/scene/camera per mounted url. Rebuilt
  // when the texture url changes so the old GPU resources are freed.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      fovRef.current,
      Math.max(1, container.clientWidth) / Math.max(1, container.clientHeight),
      0.1,
      1000,
    );
    camera.position.set(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(
      Math.max(1, container.clientWidth),
      Math.max(1, container.clientHeight),
    );
    container.appendChild(renderer.domElement);
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.touchAction = 'none';
    renderer.domElement.style.cursor = 'grab';

    // Inverted sphere: scaling x by -1 flips the winding so the camera at
    // the centre sees the (un-mirrored) inner surface with the default
    // FrontSide. Do NOT also set side: BackSide — combining the two
    // cancels out and culls every face the camera can see, leaving the
    // canvas cleared to opaque black.
    const geometry = new THREE.SphereGeometry(500, 60, 40);
    geometry.scale(-1, 1, 1);

    const material = new THREE.MeshBasicMaterial();
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    // Load the panorama via fetch + createImageBitmap rather than
    // THREE.TextureLoader. The full equirectangular PNG can be huge:
    // (a) an <img>-based decode (what TextureLoader uses) can silently
    // fail on very large images, leaving the sphere untextured (white),
    // and (b) a texture above the GPU's maxTextureSize uploads as an
    // unusable (black) texture. createImageBitmap decodes large images
    // reliably, and we downscale to maxTextureSize (aspect preserved)
    // before upload.
    let texture: THREE.Texture | null = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        const blob = await res.blob();
        const bitmap = await createImageBitmap(blob);
        if (cancelled) {
          bitmap.close();
          return;
        }
        // Always draw the decoded bitmap into a canvas (capped to the
        // GPU's max texture size, aspect preserved) and texture from the
        // canvas. Two reasons: (1) it bounds the texture so an oversized
        // image can't fail the upload, and (2) a <canvas> honours the
        // default texture.flipY, whereas an ImageBitmap used directly
        // does NOT — texturing straight off the bitmap renders the
        // panorama upside down.
        const maxSize = Math.min(renderer.capabilities.maxTextureSize || 4096, 8192);
        const scale = Math.min(1, maxSize / bitmap.width, maxSize / bitmap.height);
        const cw = Math.max(1, Math.round(bitmap.width * scale));
        const ch = Math.max(1, Math.round(bitmap.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.drawImage(bitmap, 0, 0, cw, ch);
        bitmap.close();
        const tex = new THREE.Texture(canvas);
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.needsUpdate = true;
        texture = tex;
        material.map = tex;
        material.needsUpdate = true;
      } catch (err) {
        if (!cancelled) {
          // Surface the real reason (404, decode failure, etc.) instead
          // of leaving a silently-white sphere.
          console.error('Panorama360: failed to load panorama', url, err);
        }
      }
    })();

    // --- Pointer drag → yaw/pitch deltas reported via onChange. ---
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    let activePointer: number | null = null;

    const onPointerDown = (e: PointerEvent) => {
      dragging = true;
      activePointer = e.pointerId;
      lastX = e.clientX;
      lastY = e.clientY;
      renderer.domElement.setPointerCapture(e.pointerId);
      renderer.domElement.style.cursor = 'grabbing';
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!dragging || e.pointerId !== activePointer) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      const current = anglesRef.current;
      const nextYaw = current.yaw - dx * DRAG_SPEED;
      const nextPitch = Math.max(
        -PITCH_LIMIT,
        Math.min(PITCH_LIMIT, current.pitch - dy * DRAG_SPEED),
      );
      onChangeRef.current({ yaw: nextYaw, pitch: nextPitch });
    };

    const endDrag = (e: PointerEvent) => {
      if (e.pointerId !== activePointer) return;
      dragging = false;
      activePointer = null;
      if (renderer.domElement.hasPointerCapture(e.pointerId)) {
        renderer.domElement.releasePointerCapture(e.pointerId);
      }
      renderer.domElement.style.cursor = 'grab';
    };

    // --- Wheel → zoom (field of view) reported via onFovChange. ---
    const onWheel = (e: WheelEvent) => {
      if (!onFovChangeRef.current) return;
      // Stop the modal/page from scrolling while zooming the panorama.
      e.preventDefault();
      const next = Math.min(
        MAX_FOV,
        Math.max(MIN_FOV, fovRef.current + e.deltaY * FOV_SPEED),
      );
      onFovChangeRef.current(next);
    };

    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    renderer.domElement.addEventListener('pointermove', onPointerMove);
    renderer.domElement.addEventListener('pointerup', endDrag);
    renderer.domElement.addEventListener('pointercancel', endDrag);
    renderer.domElement.addEventListener('wheel', onWheel, { passive: false });

    // --- Resize handling. ---
    const resize = () => {
      const w = Math.max(1, container.clientWidth);
      const h = Math.max(1, container.clientHeight);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);

    // --- Render loop. ---
    let frameId = 0;
    const target = new THREE.Vector3();
    const renderLoop = () => {
      frameId = requestAnimationFrame(renderLoop);
      const { yaw: y, pitch: p } = anglesRef.current;
      // Look direction from yaw/pitch (yaw around Y, pitch around X).
      target.set(
        Math.cos(p) * Math.sin(y),
        Math.sin(p),
        Math.cos(p) * Math.cos(y),
      );
      camera.lookAt(target);
      if (camera.fov !== fovRef.current) {
        camera.fov = fovRef.current;
        camera.updateProjectionMatrix();
      }
      renderer.render(scene, camera);
    };
    renderLoop();

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      renderer.domElement.removeEventListener('pointerup', endDrag);
      renderer.domElement.removeEventListener('pointercancel', endDrag);
      renderer.domElement.removeEventListener('wheel', onWheel);
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      if (texture) texture.dispose();
      renderer.dispose();
    };
  }, [url]);

  return <div ref={containerRef} className={className} />;
}
